import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismMessage, PrismPerfectScrollbar } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import HotelMarketInsightListsListItemsListItemCountry from 'lumen/components/hotel/market-insight/lists/list-items/list-item-country';

interface HotelMarketInsightListsListCountriesSearchingSignature {
  Args: {
    activeSearchesType: unknown;
    countrySearches: unknown;
    hasEmptyState: unknown;
    isLoading: unknown;
    listCssClasses: unknown;
    wheelPropagation: unknown;
  };
}

export default class HotelMarketInsightListsListCountriesSearching extends Component<HotelMarketInsightListsListCountriesSearchingSignature> {
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get countrySearches() {
    return this.args.countrySearches;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get countrySearchesFormatted() {
    // Get the highest search percentage found amongst the countries returned by the API
    const maxSearchPercentage = Math.max(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call
      ...this.countrySearches.map(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        (countrySearch) => countrySearch.searchFractionAsPercentage,
      ),
    );

    // Calculate the fraction of "Other" country searches ourselves
    let otherSearchesFraction = 1;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.countrySearches.forEach((countrySearch) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      otherSearchesFraction -= countrySearch.searchFraction;
    });

    // Take the max value between the country search percentages & other searches percentage to decide which value will be used as 100% of the bar indicator.
    const maxPercentageToUseForBar = Math.max(
      maxSearchPercentage,
      otherSearchesFraction * 100,
    );

    // Calculate the percentage that the country searches represent in the day total searches (divide by the search summary num searches)
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const result = this.countrySearches.map((countrySearch) => {
      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        countryFormatted: countrySearch.countryFormatted,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        searchFraction: countrySearch.searchFractionAsPercentage,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        averageLos: countrySearch.averageLos,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        averageLosFormatted: countrySearch.averageLosFormatted,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        airportSearches:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          countrySearch.airportSearchesWithSearchFractionRounding.sort(
            // @ts-expect-error: Incorrect type
            (a, b) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (a.search_fraction !== b.search_fraction) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                return b.search_fraction - a.search_fraction;
              }
              // Fallback on alphabetical sorting on airport code if the search percentage is the same
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
              return a.airport.localeCompare(b.airport);
            },
          ),
        // format using ember-intl, we can use the searchFraction for that since ember-intl already multiplies by 100
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        searchPercentage: this.intl.formatNumber(countrySearch.searchFraction, {
          style: 'percent',
          maximumFractionDigits: 1,
          minimumFractionDigits: 1,
        }),
        // Below is the width used for the bars. We recalculate so that the max search percentage is 100% in the bar indicator
        // Else the bars are way too low most of the time...
        valueProgressBarData: [
          {
            widthPercentage: Math.round(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              (countrySearch.searchFractionAsPercentage /
                maxPercentageToUseForBar) *
                100,
            ),
          },
        ],
        maxPercentageToUseForBar,
      };
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return result.sort((a, b) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (a.searchFraction !== b.searchFraction) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return b.searchFraction - a.searchFraction;
      }

      // Fallback on alphabetical sorting on country code if the search percentage is the same
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return a.countryFormatted.localeCompare(b.countryFormatted);
    });
  }

  <template>
    <ul class="breakdown-list">
      {{#if this.countrySearchesFormatted.length}}
        <li class="breakdown-list__item flex flex-row">
          <div
            class="breakdown-list__item__title flex flex-row justify-between"
          >
            <p class="text-200--bold">
              {{t "events.countries"}}
            </p>

            <p class="text-200--bold">
              {{t "market_insight.flight_patterns.avg_los"}}
            </p>
          </div>
        </li>

        <PrismPerfectScrollbar
          {{! @glint-expect-error: Incorrect type }}
          @wheelPropagation={{@wheelPropagation}}
          class="country-searches-list {{@listCssClasses}}"
        >
          {{#each this.countrySearchesFormatted as |countrySearch|}}
            <HotelMarketInsightListsListItemsListItemCountry
              {{! @glint-expect-error: Incorrect type }}
              @activeSearchesType={{@activeSearchesType}}
              @countrySearch={{countrySearch}}
            />
          {{/each}}
        </PrismPerfectScrollbar>
      {{else}}
        {{#if (and @hasEmptyState (not @isLoading))}}
          <div
            class="breakdown-list__empty-state-container flex flex-row items-center justify-center"
          >
            <PrismMessage
              @illustration="100x100_critical"
              @title={{t "market_insight.no_data_for_breakdown_selected_range"}}
            />
          </div>
        {{/if}}
      {{/if}}
    </ul>
  </template>
}
